import React from 'react';
import { Link } from 'react-router-dom';

// Get current date in New Zealand format
const getCurrentDate = () => {
  return new Date().toLocaleDateString('en-NZ', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="legal-container w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Privacy Policy
            </h1>
            <p className="text-gray-600">
              Last updated: {getCurrentDate()}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <div className="space-y-6">
              {/* Introduction */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
                <p className="text-gray-700 leading-relaxed">
                  Welcome to FitFlow ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, and safeguard your information when you use our fitness class booking application.
                </p>
              </section>

              {/* Information We Collect */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We collect the following personal information from you:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Name:</strong> Your first and last name for account identification and class bookings</li>
                  <li><strong>Email Address:</strong> Used for account creation, authentication, and communication</li>
                  <li><strong>Phone Number:</strong> Used for account verification and emergency contact purposes</li>
                  <li><strong>Class Bookings:</strong> Information about classes you book and attend</li>
                </ul>
              </section>

              {/* How We Use Information */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We use the collected information to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Create and manage your FitFlow account</li>
                  <li>Process your class bookings and manage your schedule</li>
                  <li>Provide customer support and respond to your inquiries</li>
                  <li>Ensure the security and proper functioning of our application</li>
                </ul>
              </section>

              {/* Data Storage and Security */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Storage and Security</h2>
                <p className="text-gray-700 leading-relaxed">
                  Your personal information is stored securely on servers hosted on a Hostinger VPS. Hostinger may operate data centers outside of New Zealand. Regardless of location, we implement appropriate technical and organizational measures to protect your data against unauthorized access, alteration, disclosure, or destruction, and we require safeguards consistent with New Zealand privacy standards. All data is encrypted in transit and at rest.
                </p>
              </section>

              {/* Information Sharing */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Information Sharing</h2>
                <p className="text-gray-700 leading-relaxed">
                  We do not sell, trade, or otherwise transfer your personal information to third parties. We do not use tracking cookies or analytics that collect personal data. Your information is only used for the purposes outlined in this policy and is not shared with external parties except as required by New Zealand law.
                </p>
              </section>

              {/* Your Rights */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Your Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Under the New Zealand Privacy Act 2020, you have the right to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of any inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request the deletion of your personal information (subject to legal obligations)</li>
                  <li><strong>Complaint:</strong> Make a complaint to the Privacy Commissioner if you believe we have breached your privacy</li>
                </ul>
              </section>

              {/* Cookies and Tracking */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Cookies and Tracking</h2>
                <p className="text-gray-700 leading-relaxed">
                  We do not use tracking cookies, analytics, or any other tracking technologies. The only cookies we use are essential session cookies required for the basic functioning of the application (such as maintaining your login session). We do not track your browsing behavior or collect usage analytics.
                </p>
              </section>

              {/* Data Retention */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Data Retention</h2>
                <p className="text-gray-700 leading-relaxed">
                  We retain your personal information only for as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will remove your personal information from our systems within 30 days, except where we are required to retain it for legal or regulatory purposes.
                </p>
              </section>

              {/* Changes to Policy */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically to stay informed about how we are protecting your information.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
                </p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-gray-700">
                    <strong>Email:</strong> younitefitness@hotmail.com<br/>
                    <strong>Subject:</strong> Privacy Policy Inquiry
                  </p>
                </div>
              </section>

              {/* Effective Date */}
              <section className="border-t pt-6">
                <p className="text-sm text-gray-500">
                  <strong>Effective Date:</strong> January 2025<br/>
                  <strong>Last Updated:</strong> {getCurrentDate()}
                </p>
              </section>
            </div>
          </div>

          {/* Back to Login */}
          <div className="text-center mt-8">
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              ← Back to Login
            </Link>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-gray-500">
              © 2025 FitFlow. <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</Link> | <Link to="/terms" className="text-blue-600 hover:text-blue-800 underline">Terms</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
